import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import heroImg1 from '../../assets/homepages-hero-img/hero-1-universities.png';
import heroImg2 from '../../assets/homepages-hero-img/hero-2-govt-exams.png';
import heroImg3 from '../../assets/homepages-hero-img/hero-3-board-practice.png';

const SLIDES = [
    { image: heroImg1, heading: 'BEST UNIVERSITY STUDY MATERIAL' },
    { image: heroImg2, heading: 'Most Effective Mock Test Series for Preparation' },
    { image: heroImg3, heading: 'Practice more, score better, and top your board exams.' }
];

const AUTO_SCROLL_MS = 11000;

// Full-width homepage hero — 3 slides, auto-advancing every 11s (paused on
// hover), manual prev/next arrows + dot indicators, same 2 CTA buttons on
// every slide. Each slide is an absolutely-positioned layer cross-fading via
// opacity (no layout shift, no reflow — just a CSS transition), so the
// slider's own height (set once on the outer <section>) is what actually
// drives responsiveness; the image/text inside just fills it.
const HeroSlider = () => {
    const [current, setCurrent] = useState(0);
    const [paused, setPaused] = useState(false);

    const goTo = useCallback((index) => {
        setCurrent(((index % SLIDES.length) + SLIDES.length) % SLIDES.length);
    }, []);

    const timerRef = useRef(null);
    useEffect(() => {
        if (paused) return undefined;
        timerRef.current = setInterval(() => {
            setCurrent((prev) => (prev + 1) % SLIDES.length);
        }, AUTO_SCROLL_MS);
        return () => clearInterval(timerRef.current);
    }, [paused]);

    return (
        <section
            className="relative w-full h-[350px] sm:h-[420px] md:h-[500px] lg:h-[600px] overflow-hidden bg-gray-900"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {SLIDES.map((slide, index) => (
                <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === current ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                    aria-hidden={index !== current}
                >
                    <img
                        src={slide.image}
                        alt={slide.heading}
                        className="w-full h-full object-cover"
                    />
                    {/* Left-to-right gradient — text sits in the darker left
                        side regardless of the image's own content/brightness,
                        while the right side stays visible. More reliable
                        across 3 different photos than a vertical gradient
                        combined with vertically-centered text. */}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-black/10"></div>

                    <div className="absolute inset-0 flex items-center">
                        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 w-full">
                            <div className="max-w-2xl">
                                <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight drop-shadow-lg">
                                    {slide.heading}
                                </h1>
                                <div className="mt-5 sm:mt-8 flex flex-wrap gap-3 sm:gap-4">
                                    <Link
                                        to="/university-study-material"
                                        className="inline-flex items-center px-5 sm:px-7 py-2.5 sm:py-3 bg-white text-indigo-700 font-bold rounded-xl hover:bg-gray-100 transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-105 text-sm sm:text-base"
                                    >
                                        Start Learning
                                    </Link>
                                    <Link
                                        to="/mock-tests-new"
                                        className="inline-flex items-center px-5 sm:px-7 py-2.5 sm:py-3 bg-transparent text-white font-bold rounded-xl border-2 border-white hover:bg-white hover:text-indigo-700 transition-all duration-200 hover:scale-105 text-sm sm:text-base"
                                    >
                                        Take Mock Test
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}

            <button
                type="button"
                onClick={() => goTo(current - 1)}
                aria-label="Previous slide"
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-full bg-black/30 hover:bg-black/50 text-white backdrop-blur-sm transition-all duration-200"
            >
                <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
            </button>
            <button
                type="button"
                onClick={() => goTo(current + 1)}
                aria-label="Next slide"
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-full bg-black/30 hover:bg-black/50 text-white backdrop-blur-sm transition-all duration-200"
            >
                <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
            </button>

            <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                {SLIDES.map((_, index) => (
                    <button
                        key={index}
                        type="button"
                        onClick={() => goTo(index)}
                        aria-label={`Go to slide ${index + 1}`}
                        className={`h-2 rounded-full transition-all duration-300 ${index === current ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/75'}`}
                    />
                ))}
            </div>
        </section>
    );
};

export default HeroSlider;
